/**
 * B2B Checkout Metadata Helpers [BNSHO-1127]
 * Line item properties and cart attributes for B2B/DTC alignment.
 */

(function () {
  'use strict';

  const DIGITAL_SKU_TYPES = [
    'digitalAudiobook',
    'Warranty',
    'eBook',
    'eMagazine',
    'eTextbook',
    'nookApp',
    'membership',
    'eGiftCards',
    'digitalVideo',
  ];

  /**
   * Generates a unique identifier for a line item property link (UUID v4).
   * @returns {string}
   */
  function createLineItemPropertyLink() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    const chars = '0123456789abcdef';
    const template = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx';
    return template.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return chars[v];
    });
  }

  /**
   * Determines fulfillment type based on product properties.
   * @param {{ requiresShipping: boolean, isSubscription: boolean }} input
   * @returns {'ship' | 'pickup' | 'electronic' | 'subscription'}
   */
  function deriveFulfillmentType({ requiresShipping, isSubscription }) {
    if (requiresShipping === false) {
      return 'electronic';
    }
    if (isSubscription) {
      return 'subscription';
    }
    return 'ship';
  }

  /**
   * Determines _ReferenceNumber2 value based on SKU type (digital = '0', non-digital = '1').
   * @param {string|null} skuType
   * @returns {'0' | '1'}
   */
  function getReferenceNumber2(skuType) {
    if (!skuType) return '1';
    return DIGITAL_SKU_TYPES.includes(skuType) ? '0' : '1';
  }

  /**
   * Gets _effs value from product (isEligibleForFreeShipping).
   * Line item property for guest free-shipping threshold; only lines with _effs 'true' or '1' count.
   * @param {Object} product - Product object with metafields (object bnb.isEligibleForFreeShipping or array)
   * @returns {string|undefined} Value for _effs (e.g. 'true', 'false', '1') or undefined
   */
  function getEffsFromProduct(product) {
    if (!product?.metafields) return undefined;
    const mf = product.metafields;
    if (Array.isArray(mf)) {
      const field = mf.find(function (m) {
        return m && m.namespace === 'bnb' && (m.key === 'isEligibleForFreeShipping' || m.key === 'is_eligible_for_free_shipping');
      });
      return field?.value != null ? String(field.value) : undefined;
    }
    const bnb = mf.bnb || mf['bnb'];
    if (!bnb) return undefined;
    const field = bnb.isEligibleForFreeShipping ?? bnb.is_eligible_for_free_shipping;
    const val = field?.value ?? field;
    return val != null && val !== '' ? String(val) : undefined;
  }

  /**
   * Extracts SKU type from product metafields (namespace bnb, key skuType).
   * Per the implementation guide: used when product.metafields is available in JS (e.g. headless
   * or Storefront API). In headed theme, skuType is passed from Liquid via data-product-sku-type.
   * @param {Object} metafields - Product metafields object or null
   * @returns {string|null}
   */
  function getSkuTypeFromMetafields(metafields) {
    if (!metafields) return null;
    const bnb = metafields.bnb || metafields['bnb'];
    if (!bnb) return null;
    return bnb.sku_type || bnb.skuType || null;
  }

  /**
   * Checks if product is subscription (by type or metafields).
   * @param {string} productType
   * @param {Object} [metafields]
   * @returns {boolean}
   */
  function isSubscriptionProduct(productType, metafields) {
    if (metafields) {
      const sub = (metafields.subscription || metafields['subscription']) || {};
      if (sub.type || sub.product_subscription) return true;
    }
    if (productType) {
      const t = (productType || '').toLowerCase();
      if (t.includes('subscription')) return true;
    }
    return false;
  }

  function getCookie(name) {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  }

  function setCookie(name, value, options = {}) {
    let str = encodeURIComponent(name) + '=' + encodeURIComponent(value);
    if (options.maxAge) str += '; max-age=' + options.maxAge;
    if (options.path) str += '; path=' + options.path;
    if (options.sameSite) str += '; SameSite=' + options.sameSite;
    document.cookie = str;
  }

  /**
   * Gets or creates visitor ID (UUID), persisted in cookie and localStorage.
   * @returns {string}
   */
  function getOrCreateVisitorId() {
    let visitorId = getCookie('visitorId');
    if (!visitorId) visitorId = (typeof localStorage !== 'undefined' && localStorage.getItem('visitorId')) || null;
    if (!visitorId) {
      visitorId = createLineItemPropertyLink();
      setCookie('visitorId', visitorId, { maxAge: 365 * 24 * 60 * 60, path: '/' });
      if (typeof localStorage !== 'undefined') localStorage.setItem('visitorId', visitorId);
    }
    return visitorId;
  }

  /**
   * Determines entered_by value based on viewport (desktop >= 1024 = basicSite).
   * @returns {'bnMobileSite' | 'basicSite'}
   */
  function getEnteredBy() {
    const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1024;
    return isDesktop ? 'basicSite' : 'bnMobileSite';
  }

  /**
   * Fetches membership data from API.
   * @param {string} email
   * @returns {Promise<Object|null>}
   */
  async function getMembershipData(email) {
    if (!email) return null;
    try {
      const url = '/api/membership/member?email=' + encodeURIComponent(email) + '&brand=BN&requestType=L';
      const response = await fetch(url);
      const data = await response.json();
      return data || null;
    } catch (e) {
      console.error('Error fetching membership data:', e);
      return null;
    }
  }

  /**
   * Checks if customer is employee from membership data.
   * @param {Object|null} membershipData
   * @returns {boolean}
   */
  function isEmployee(membershipData) {
    if (!membershipData?.Membership?.Employee) return false;
    const status = membershipData.Membership.Employee.Status;
    return !!(status && status !== 'NULL');
  }

  /**
   * Builds line item properties for add-to-cart (new line).
   * @param {{ variant: Object, skuType: string|null, productType: string, productMetafields?: Object, product?: Object, fulfillmentTypeOverride?: string, effs?: string }} opts
   * @param {{ existingLineItemPropertyLink?: string|null }} [existing] - When updating quantity, pass existing link to preserve it.
   * @returns {Array<{ key: string, value: string }>}
   */
  function buildLineItemProperties(opts, existing) {
    const { variant, skuType, productType, productMetafields, product, fulfillmentTypeOverride, effs } = opts;
    const existingLink = existing && existing.existingLineItemPropertyLink;
    const requiresShipping = variant && variant.requires_shipping !== undefined ? variant.requires_shipping : true;
    const isSubscription = isSubscriptionProduct(productType, productMetafields);
    let fulfillmentType = fulfillmentTypeOverride || deriveFulfillmentType({ requiresShipping, isSubscription });
    const skuTypeResolved = (skuType != null && skuType !== '') ? skuType : getSkuTypeFromMetafields(productMetafields);
    const effsResolved = (effs != null && effs !== '') ? String(effs) : getEffsFromProduct(product);

    const props = [];

    props.push({ key: '_fulfillment_type', value: fulfillmentType });
    if (fulfillmentType === 'pickup') {
      props.push({ key: '_ReferenceNumber3', value: '1' });
    }
    props.push({ key: '_ReferenceNumber2', value: getReferenceNumber2(skuTypeResolved) });
    const link = existingLink != null && existingLink !== '' ? existingLink : createLineItemPropertyLink();
    props.push({ key: '_lineItemPropertyLink', value: link });
    if (effsResolved != null && effsResolved !== '') {
      props.push({ key: '_effs', value: effsResolved });
    }

    return props;
  }

  /**
   * Updates cart attributes (entered_by, _visitorId, membership fields). Batches in one request.
   * @param {Object} [cart] - Current cart object (optional; will fetch if not provided)
   * @returns {Promise<void>}
   */
  async function updateCartAttributes(cart) {
    if (typeof window === 'undefined' || !window.routes || !window.routes.cart_update_url) return;
    const customer = window.customer || null;
    const customerEmail = customer && customer.email ? customer.email : null;

    let currentCart = cart;
    if (!currentCart) {
      try {
        const res = await fetch(window.routes.cart_url + '.js');
        currentCart = await res.json();
      } catch (e) {
        console.error('Error fetching cart for attributes update:', e);
        return;
      }
    }

    const attributes = {};
    attributes.entered_by = getEnteredBy();
    attributes._visitorId = getOrCreateVisitorId();

    if (customerEmail) {
      const membershipData = await getMembershipData(customerEmail);
      const emp = membershipData ? isEmployee(membershipData) : false;

      attributes._ReferenceNumber1 = (membershipData?.Membership?.MemberID) || '';
      if (!emp) {
        attributes._MAO_MemberTermType = (membershipData?.Membership?.TermType?.ID) || '';
      } else {
        attributes._MAO_MemberTermType = '';
      }
      if (emp) {
        attributes._EmployeeDiscountID = (membershipData?.Membership?.Employee?.EmployeeDiscountID) || '';
        attributes._EmployeeCardNumber = (membershipData?.Membership?.Employee?.EmployeeCardNumber) || '';
      } else {
        attributes._EmployeeDiscountID = '';
        attributes._EmployeeCardNumber = '';
      }
    } else {
      attributes._ReferenceNumber1 = '';
      attributes._MAO_MemberTermType = '';
      attributes._EmployeeDiscountID = '';
      attributes._EmployeeCardNumber = '';
    }

    const needsUpdate = Object.keys(attributes).some((key) => {
      const cur = (currentCart.attributes && currentCart.attributes[key]) || '';
      return String(attributes[key] || '') !== String(cur);
    });
    if (!needsUpdate) return;

    try {
      await fetch(window.routes.cart_update_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }),
      });
    } catch (e) {
      console.error('Error updating cart attributes:', e);
    }
  }

  /**
   * Parses line item properties from cart item row (data-line-item-properties).
   * Shopify cart/change.js expects properties to be a Hash (object), not an array.
   * Liquid item.properties | json can serialize as array of [key, value] pairs; normalize to object.
   * @param {HTMLElement} row - Cart item row (e.g. #CartItem-1 or #CartDrawer-Item-1)
   * @returns {Object} - Properties object for cart/change.js
   */
  function getLineItemPropertiesFromRow(row) {
    if (!row) return {};
    const raw = row.getAttribute && row.getAttribute('data-line-item-properties');
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw);
      if (!parsed) return {};
      if (Array.isArray(parsed)) {
        const obj = {};
        parsed.forEach(function (pair) {
          if (Array.isArray(pair) && pair.length >= 2) {
            obj[String(pair[0])] = pair[1] != null ? String(pair[1]) : '';
          }
        });
        return obj;
      }
      if (typeof parsed === 'object' && parsed !== null) {
        return parsed;
      }
      return {};
    } catch (e) {
      return {};
    }
  }

  /**
   * Ensures cart attributes are updated (e.g. after cart load or add/change). Idempotent.
   */
  function ensureCartAttributesUpdated() {
    updateCartAttributes().catch(function (err) {
      console.error('ensureCartAttributesUpdated:', err);
    });
  }

  // Public API
  window.CartHelpers = {
    createLineItemPropertyLink,
    deriveFulfillmentType,
    getReferenceNumber2,
    getSkuTypeFromMetafields,
    getEffsFromProduct,
    isSubscriptionProduct,
    getOrCreateVisitorId,
    getEnteredBy,
    getMembershipData,
    isEmployee,
    buildLineItemProperties,
    updateCartAttributes,
    getLineItemPropertiesFromRow,
    ensureCartAttributesUpdated,
  };
})();
